import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { AlertDetailResponse, AlertUpdatePayload } from '@/types/alerts';

export function useAlertDetail(
  alertId: number | undefined,
): UseQueryResult<AlertDetailResponse, Error> {
  return useQuery<AlertDetailResponse, Error>({
    queryKey: ['alert', alertId],
    enabled: alertId != null,
    queryFn: async () => {
      const resp = await api.get<AlertDetailResponse>(`/alerts/${alertId}`);
      return resp.data;
    },
  });
}

/** PATCH /alerts/{id} — used by the disposition buttons. */
export function useUpdateAlert(alertId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<AlertDetailResponse, Error, AlertUpdatePayload>({
    mutationFn: async (payload) => {
      const resp = await api.patch<AlertDetailResponse>(`/alerts/${alertId}`, payload);
      return resp.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(['alert', alertId], data);
      qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

/** POST /alerts/{id}/escalate — used by the [Escalate] button. */
export function useEscalateAlert(alertId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<AlertDetailResponse, Error, { reason?: string }>({
    mutationFn: async (payload) => {
      const resp = await api.post<AlertDetailResponse>(
        `/alerts/${alertId}/escalate`,
        payload,
      );
      return resp.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(['alert', alertId], data);
      qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}
